import { createElement, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

const SOURCE_LANG = "fr";
const CACHE_PREFIX = "xlate:v1:";

// Simple FNV-1a hash so we get short, stable keys
const hash = (s: string) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(36);
};

const cacheKey = (lang: string, text: string) =>
  `${CACHE_PREFIX}${lang}:${hash(text)}`;

const readCache = (lang: string, text: string): string | null => {
  try {
    return localStorage.getItem(cacheKey(lang, text));
  } catch {
    return null;
  }
};

const writeCache = (lang: string, text: string, value: string) => {
  try {
    localStorage.setItem(cacheKey(lang, text), value);
  } catch {}
};

async function translateBatchRaw(texts: string[], targetLang: string, sourceLang: string = SOURCE_LANG): Promise<string[]> {
  if (!texts.length) return [];
  const { data, error } = await supabase.functions.invoke("translate-content", {
    body: { texts, targetLang, sourceLang },
  });
  if (error) {
    console.error("[useAITranslate] edge function error", { targetLang, error });
    throw error;
  }
  if ((data as any)?.error) {
    console.error("[useAITranslate] gateway error", data);
    throw new Error(String((data as any).error));
  }
  const out: string[] = Array.isArray((data as any)?.translations)
    ? (data as any).translations
    : [];
  return texts.map((t, i) => out[i] ?? t);
}

// ---- Micro-batching ----
// All requests issued within a 60 ms window for the same target lang are
// coalesced into a single HTTP call. This lets us wrap dozens of strings with
// <T> without firing dozens of edge function invocations.
type Bucket = {
  texts: Set<string>;
  waiters: Array<{ keys: string[]; resolve: (v: string[]) => void; reject: (e: unknown) => void }>;
  timer: ReturnType<typeof setTimeout> | null;
};
const buckets = new Map<string, Bucket>();
const BATCH_WINDOW_MS = 60;

function scheduleTranslate(keys: string[], targetLang: string, sourceLang: string = SOURCE_LANG): Promise<string[]> {
  const bucketKey = `${sourceLang}|${targetLang}`;
  return new Promise((resolve, reject) => {
    let bucket = buckets.get(bucketKey);
    if (!bucket) {
      bucket = { texts: new Set(), waiters: [], timer: null };
      buckets.set(bucketKey, bucket);
    }
    keys.forEach((k) => bucket!.texts.add(k));
    bucket.waiters.push({ keys, resolve, reject });

    if (bucket.timer) clearTimeout(bucket.timer);
    bucket.timer = setTimeout(async () => {
      const b = buckets.get(bucketKey);
      if (!b) return;
      buckets.delete(bucketKey);
      const all = Array.from(b.texts);
      try {
        const out = await translateBatchRaw(all, targetLang, sourceLang);
        const map = new Map<string, string>();
        all.forEach((t, i) => map.set(t, out[i] ?? t));
        b.waiters.forEach((w) => w.resolve(w.keys.map((k) => map.get(k) ?? k)));
      } catch (e) {
        b.waiters.forEach((w) => w.reject(e));
      }
    }, BATCH_WINDOW_MS);
  });
}

/**
 * Translate one or many strings via Lovable AI (Gemini), cached in localStorage.
 * Returns the original text(s) when language === source ("fr") or while loading.
 * Reviews and user-generated authored content should NOT be passed here.
 */
export function useAITranslate<T extends string | string[]>(input: T, sourceLang: string = SOURCE_LANG): T {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || sourceLang).slice(0, 2);

  const isArray = Array.isArray(input);
  const arr: string[] = isArray ? (input as string[]) : [input as string];

  const compute = (): string[] => {
    if (lang === sourceLang) return arr;
    return arr.map((t) => (t ? readCache(lang, t) ?? t : t));
  };

  const [out, setOut] = useState<string[]>(compute);

  useEffect(() => {
    if (lang === sourceLang) {
      setOut(arr);
      return;
    }
    // Start from cache, fall back to source text while fetching.
    const initial = arr.map((t) => (t ? readCache(lang, t) : ""));
    setOut(initial.map((v, i) => v ?? arr[i]));

    const need = arr
      .map((text, idx) => ({ idx, text }))
      .filter((n) => n.text && !readCache(lang, n.text));

    if (need.length === 0) return;

    let cancelled = false;
    scheduleTranslate(need.map((n) => n.text), lang, sourceLang)
      .then((res) => {
        need.forEach((n, i) => writeCache(lang, n.text, res[i] ?? n.text));
        if (cancelled) return;
        setOut(arr.map((t) => (t ? readCache(lang, t) ?? t : t)));
      })
      .catch((e) => {
        console.error("[useAITranslate] translation failed", { lang, texts: need.map((n) => n.text), error: e });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, JSON.stringify(arr)]);

  return (isArray ? out : out[0]) as T;
}

/**
 * Inline translator for literal UI strings.
 * Usage: <T>Réserver maintenant</T>
 */
export const T = ({ children }: { children: string }) => {
  const v = useAITranslate(children);
  return createElement(Fragment, null, v);
};